/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'

// Components
import {Drawer, DrawerOverlay, DrawerContent, Alert, useBreakpointValue} from '@chakra-ui/react'

// Others
import LoadingSpinner from '../loading-spinner'

import {API_ERROR_MESSAGE, HEADER_MENU_CONTENT_ID} from '../../constants'
import {useNavigationContent} from '../../hooks/useScapiCustomApi'
import DrawerMenuMobile from './drawer-menu-mobile'
import DrawerMenuDesktop from './drawer-menu-desktop'
import useMultiSite from '../../hooks/use-multi-site'

/**
 * Convert the navigation items from the API:
 * add the key as id to each item on lvl0 if it's not already there
 *
 * @param {Object} items
 * @return {Array}
 */
const convertNavigationItems = (items) => {
    const itemsList = Object.entries(items || {}).map(([key, value]) => ({
        id: key,
        ...value
    }))

    if (!itemsList.length) {
        return []
    }

    return itemsList
}

/**
 * This is the navigation component used for mobile devices (phone and tablet). It's
 * main usage is to navigate from one category to the next, but also homes links to
 * support, log in and out actions, as support links.
 */
const DrawerMenu = ({
    selected: selectedLvl0Id,
    setSelected: setSelectedLvl0Id,
    isOpen,
    onClose,
    onMyAccountClick,
    headerVariant = 'default'
}) => {
    const intl = useIntl()
    const {locale} = useMultiSite()
    const [drawerRef, setDrawerRef] = useState(null)
    // flag to trigger the fetching of the navigation items
    const [isNavigationItemsNeeded, setIsNavigationItemsNeeded] = useState(isOpen)

    // when the drawer is opened, set the flag to fetch the navigation items
    useEffect(() => {
        if (isOpen) {
            setIsNavigationItemsNeeded(true)
        }
    }, [isOpen])

    const {
        data: items,
        isLoading: loading,
        error
    } = useNavigationContent(
        {
            parameters: {locale: locale.id}
        },
        {
            enabled: isNavigationItemsNeeded,
            select: (data) => convertNavigationItems(data),
            onError: () => {
                console.error('Error fetching navigation items', error)
                // reset the flag, so next time the drawer is opened, we try to fetch the items again
                setIsNavigationItemsNeeded(false)
            }
        }
    )

    const isMobile = useBreakpointValue({base: true, lg: false})

    // find the selected lvl0 item that corresponds to the selectedLvl0Id
    const selectedLvl0 = useMemo(() => {
        if (selectedLvl0Id && items) {
            for (const item of items) {
                if (item.id === selectedLvl0Id) {
                    return item
                }
            }
        }
    }, [selectedLvl0Id, items])

    // keep track of selected lvl1 item
    const [selectedLvl1, setSelectedLvl1] = useState(null)

    // clear selected lvl1 item when the selected lvl0 item changes and lvl1 is not from lvl0
    useEffect(() => {
        setSelectedLvl1((current) => (selectedLvl0?.items?.includes(current) ? current : null))
    }, [selectedLvl0])

    // helper for setting the selected item from lvl0
    const setSelectedLvl0 = useCallback(
        (item) => {
            setSelectedLvl0Id?.(item?.id)
            setSelectedLvl1(null)
        },
        [setSelectedLvl0Id]
    )

    // reset the selected item when the drawer is closed
    useEffect(() => {
        if (!isOpen) {
            setSelectedLvl1(null)
        }
    }, [isOpen])

    useEffect(() => {
        if (!drawerRef || isMobile) return

        drawerRef.removeAttribute('role')
        drawerRef.removeAttribute('aria-modal')

        //removing aria-describedby directly doesn't work, that's why we use a MutationObserver
        const observer = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                if (
                    mutation.type === 'attributes' &&
                    mutation.attributeName === 'aria-describedby'
                ) {
                    drawerRef.removeAttribute('aria-describedby')
                }
            }
        })

        observer.observe(drawerRef, {
            attributes: true
        })

        return () => observer.disconnect()
    }, [drawerRef, isMobile])

    // skip rendering the drawer until we have items or either error/loading state to report
    if (!items && !error && !loading) {
        return null
    }

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            placement="left"
            size="navigation"
            variant="navigation"
            headerVariant={headerVariant}
            isFullHeight={false}
            scrollBehavior="inside"
            trapFocus={false}
        >
            <DrawerOverlay id={HEADER_MENU_CONTENT_ID}>
                <DrawerContent
                    ref={setDrawerRef}
                    aria-label={
                        isMobile
                            ? intl.formatMessage({
                                  id: 'header.button.assistive_msg.menu',
                                  defaultMessage: 'Menu'
                              })
                            : undefined
                    }
                >
                    {loading ? (
                        <LoadingSpinner />
                    ) : error ? (
                        <Alert status="error" variant="solid">
                            <FormattedMessage {...API_ERROR_MESSAGE} />
                        </Alert>
                    ) : !items ? null : isMobile ? (
                        <DrawerMenuMobile
                            items={items}
                            selectedLvl0={selectedLvl0}
                            selectedLvl1={selectedLvl1}
                            setSelectedLvl0={setSelectedLvl0}
                            setSelectedLvl1={setSelectedLvl1}
                            onMyAccountClick={onMyAccountClick}
                            onClose={onClose}
                        />
                    ) : (
                        <DrawerMenuDesktop
                            items={items}
                            selectedLvl0={selectedLvl0}
                            selectedLvl1={selectedLvl1}
                            setSelectedLvl0={setSelectedLvl0}
                            setSelectedLvl1={setSelectedLvl1}
                        />
                    )}
                </DrawerContent>
            </DrawerOverlay>
        </Drawer>
    )
}

DrawerMenu.propTypes = {
    /**
     * The currently selected item from header and first level of items
     */
    selected: PropTypes.string,
    /**
     * Function called when an item is selected.
     */
    setSelected: PropTypes.func,
    /**
     * The opened state of the drawer.
     */
    isOpen: PropTypes.bool,
    /**
     * Function called when the drawer is dismissed.
     */
    onClose: PropTypes.func,
    /**
     * Function called when the login button is clicked.
     */
    onMyAccountClick: PropTypes.func,
    /**
     * Header variant used for header height calcs
     */
    headerVariant: PropTypes.string
}

export default DrawerMenu
